import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import styles from './ConversionBar.module.scss';
import { HighLevelStatus, shouldTreatAsLiveEvent } from '@/utils/highLevelStatus';
import { useSalesStatus } from '../_shared/contexts/SalesStatusContext';
import { useMediaQuery } from '@/hooks';
import { MEDIA_QUERIES } from '@/components/_shared/constants';
import { LiveEventPanelInfo } from './LiveEvent/LiveEventPanelInfo';
import { GeneralEventPanelInfo } from './GeneralEventPanelInfo/GeneralEventPanelInfo';
import { DiscountSignal } from './DiscountSignal/DiscountSignal';
import { useBasicInfo } from '../_shared/contexts/BasicInfoContext';
import { useShouldDisplayRsvp } from './hooks/useShouldDisplayRsvp';
import { RSVPPanel } from './RSVP/RSVPPanel';
import { ExternalTicketsPanel } from './ExternalTicketsPanel/ExternalTicketsPanel';

type ConversionBarProps = {
  isPositionCalculated?: boolean;
};

export const ConversionBar: React.FC<ConversionBarProps> = ({ isPositionCalculated = true }) => {
  const isDesktop = useMediaQuery(`(min-width: ${MEDIA_QUERIES.LARGE_UP_BREAKPOINT}px)`);

  const wrapperClasses = classNames(styles.wrapper, {
    [styles['relative']]: isDesktop,
    [styles['hidden']]: !isPositionCalculated,
  });
  const [isDiscountSignalVisible, setIsDiscountSignalVisible] = useState(false);

  const { shouldDisplayRsvp, mipaToken } = useShouldDisplayRsvp();
  const { displayStatus } = useSalesStatus();

  const { endDate, hasDonationTicketsAvailable = false, externalTicketing } = useBasicInfo();
  const externalTicketsAreAvailable = externalTicketing?.externalTicketsAreAvailable ?? false;
  const isLiveEvent = shouldTreatAsLiveEvent(displayStatus, endDate?.utc, hasDonationTicketsAvailable);
  const isSalesEndedWithDonations = displayStatus === HighLevelStatus.SALES_ENDED && hasDonationTicketsAvailable;

  const infoPanel = useMemo(() => {
    if (shouldDisplayRsvp) {
      return <RSVPPanel mipaToken={mipaToken} />;
    }
    if (externalTicketsAreAvailable) {
      return <ExternalTicketsPanel />;
    }
    return isLiveEvent && !isSalesEndedWithDonations ? (
      <LiveEventPanelInfo showDiscountSignal={setIsDiscountSignalVisible} />
    ) : (
      <GeneralEventPanelInfo showSalesEndedDonationPanel={isSalesEndedWithDonations && isLiveEvent} />
    );
  }, [shouldDisplayRsvp, mipaToken, externalTicketsAreAvailable, isLiveEvent, isSalesEndedWithDonations]);

  return (
    <div className={wrapperClasses} data-testid="conversion-bar">
      {isDiscountSignalVisible && <DiscountSignal />}
      <div className={styles.content}>
        <div className={styles.infoPanelWrapper} data-testid="conversion-bar-panel-info">
          {infoPanel}
        </div>
      </div>
    </div>
  );
};
